/**
 * Changing your own password, from Settings.
 *
 * Posts to `/api/session/password`, beside sign-in rather than under
 * `/api/v1`, because a wrong current password has to count towards the same
 * lockout sign-in uses. On success the server ends every other session and
 * hands this device a fresh one, which is what the confirmation says.
 *
 * Somebody who signs in with Google has no password here; the server answers
 * 409 and the row says so instead of showing a form they cannot use.
 */

import { useState } from 'react'
import type { FormEvent } from 'react'
import { api as request } from '../../api'
import { Button } from '../../components/Button'
import { useToast } from '../../context/ToastContext'
import { apiErrorMessage } from '../../lib/api'

export function PasswordRow() {
  const { showToast } = useToast()
  const [open, setOpen] = useState(false)
  const [current, setCurrent] = useState('')
  const [next, setNext] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  function close() {
    setOpen(false)
    setCurrent('')
    setNext('')
    setError(null)
  }

  async function submit(e: FormEvent) {
    e.preventDefault()
    if (saving) return
    setSaving(true)
    setError(null)
    try {
      await request('/session/password', {
        method: 'POST',
        body: { currentPassword: current, newPassword: next },
        unversioned: true,
      })
      close()
      showToast('Password changed. You are still signed in here, and signed out everywhere else.')
    } catch (err) {
      setError(apiErrorMessage(err))
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="py-4">
      <div className="flex items-center justify-between gap-4">
        <div>
          <p className="text-sm font-medium text-shell-text-active">Password</p>
          <p className="mt-0.5 text-xs text-shell-text">
            For signing in without Google. Changing it signs you out on every other device.
          </p>
        </div>
        {!open && (
          <Button variant="secondary" size="sm" onClick={() => setOpen(true)}>
            Change
          </Button>
        )}
      </div>

      {open && (
        <form onSubmit={submit} className="mt-4 flex max-w-sm flex-col gap-3">
          <div>
            <label className="field-label" htmlFor="password-current">
              Current password
            </label>
            <input
              id="password-current"
              type="password"
              autoComplete="current-password"
              className="field-input"
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
              required
            />
          </div>
          <div>
            <label className="field-label" htmlFor="password-new">
              New password
            </label>
            <input
              id="password-new"
              type="password"
              autoComplete="new-password"
              className="field-input"
              value={next}
              onChange={(e) => setNext(e.target.value)}
              minLength={12}
              required
            />
            <p className="mt-1 text-xs text-shell-text">A sentence you will remember works better than a short word.</p>
          </div>
          {error !== null && (
            <p role="alert" className="text-sm text-danger-600">
              {error}
            </p>
          )}
          <div className="flex gap-2">
            <Button type="submit" size="sm" disabled={saving || current === '' || next === ''}>
              {saving ? 'Saving…' : 'Change password'}
            </Button>
            <Button type="button" variant="ghost" size="sm" onClick={close}>
              Cancel
            </Button>
          </div>
        </form>
      )}
    </div>
  )
}
